import { useEffect, useContext, useState, useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { SttTabs } from '../../../sporttia/SttTabs';
import { AppContext, SttFullDialog } from '../../../sporttia/all';
import { useCrud } from '../../../lib/hooks';
import EventDetails from './EventDetails';
import EventCategories from './EventCategories';
import EventParticipants from './EventParticipants';
import EventTeams from './EventTeams';
import EventPeriods from './EventPeriods';
import EventForm from './EventForm';
import EventDocuments from './EventDocuments';

type Category = {
    id?: string | number;
    name: string;
    description?: string;
    enrollment?: number | string;
    maxParticipants?: number | string;
    maxTeams?: number | string;
    mandatory?: { id: string | number; name?: string };
    numTeams?: number;
    numParticipants?: number;
    trash?: boolean;
};

type Sport = {
    id: string | number;
    name?: string;
};

type FormField = unknown;

type EventType = {
    id?: string | number;
    name: string;
    description?: string;
    kind?: string;
    difficulty?: string;
    status: string;
    maxParticipants?: number | string;
    onlyCertified?: boolean;
    showParticipants?: boolean;
    signupOnline?: boolean;
    maxPlayers?: number | string;
    type: string;
    sport: Sport;
    poster?: { id?: string | number };
    inscIni: string;
    inscEnd: string;
    ini: string;
    end: string;
    paymentForms?: string[];
    categories?: Category[];
    form?: FormField[];
    trash?: boolean | number;
    teamable?: boolean;
};

type Props = {
    id: string | number;
    trash?: boolean | number;
    onChange?: (event: EventType) => void;
    onClose?: () => void;
    disableUrlPath?: boolean;
    initialTabKey?: string;
};

export default function Event({
    id,
    trash,
    onChange,
    onClose,
    disableUrlPath,
    initialTabKey,
}: Props): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(id !== 'create');
    const [error, setError] = useState<string | null>(null);
    const [event, setEvent, , Get] = useCrud<EventType>({
        name: '',
        type: 'NORMAL',
        status: 'ACTIVE',
        inscIni: '',
        inscEnd: '',
        ini: '',
        end: '',
        sport: { id: 0 },
    });

    useEffect(() => {
        if (!isNaN(parseInt(id as string))) {
            loadEvent();
        } else if (id === 'create') {
            setLoading(false);
            setError(null);
            const now = new Date();
            const todayStart = format(now, 'yyyy-MM-dd') + ' 08:00:00';
            const todayEnd = format(now, 'yyyy-MM-dd') + ' 23:30:00';
            setEvent({
                name: '',
                kind: '',
                type: 'NORMAL',
                status: 'ACTIVE',
                teamable: false,
                inscIni: todayStart,
                inscEnd: todayEnd,
                ini: todayStart,
                end: todayEnd,
                paymentForms: [],
                sport: {
                    id: 0,
                },
            });
        }
    }, [id]);

    function loadEvent(): void {
        setLoading(true);
        setError(null);
        Get(`/events/${id}`, { trash })
            .then((response: unknown) => {
                const result = response as { event?: EventType };
                if (result.event) {
                    setEvent(result.event);
                    cxt?.setHeader({
                        title: result.event?.name,
                    });
                } else {
                    setError(t('error.eventNotFound'));
                }
            })
            .catch(() => {
                setError(t('error.loadingEvent'));
            })
            .finally(() => {
                setLoading(false);
            });
    }

    // Define tabs configuration with keys for URL routing
    // Must be before early returns to maintain hooks order
    const tabsConfig = useMemo(
        () => [
            { key: 'detail', show: true },
            { key: 'categories', show: !!event?.id },
            { key: 'participants', show: !!(event?.id && !event?.teamable) },
            { key: 'teams', show: !!(event?.id && event?.teamable) },
            { key: 'timetables', show: !!event?.id },
            { key: 'form', show: !!event?.id },
            { key: 'documents', show: !!event?.id },
        ],
        [event?.id, event?.teamable],
    );

    // Get only visible tab keys
    const visibleTabKeys = useMemo(
        () => tabsConfig.filter((tab) => tab.show).map((tab) => tab.key),
        [tabsConfig],
    );

    // Calculate initial tab index from key
    const computedInitialTab = useMemo(() => {
        if (initialTabKey) {
            const index = visibleTabKeys.indexOf(initialTabKey);
            return index >= 0 ? index : 0;
        }
        return 0;
    }, [initialTabKey, visibleTabKeys]);

    // Show loading state while fetching event data
    if (loading) {
        return (
            <SttFullDialog open onClose={onClose} title={t('Loading')}>
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                >
                    <CircularProgress />
                </Box>
            </SttFullDialog>
        );
    }

    // Show error state if event failed to load
    if (error || (!event?.name && id !== 'create')) {
        return (
            <SttFullDialog open onClose={onClose} title={t('Error')}>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                    gap={2}
                >
                    <ErrorOutlineIcon
                        sx={{ fontSize: 48, color: 'error.main' }}
                    />
                    <Typography color="error">
                        {error || t('error.eventNotFound')}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onClose}
                    >
                        {t('Close')}
                    </Button>
                </Box>
            </SttFullDialog>
        );
    }

    if (!event) {
        return null;
    }

    // Handle tab change - update URL
    const handleTabChange = (tabIndex: number) => {
        const tabKey = visibleTabKeys[tabIndex];
        if (tabKey && event.id && !disableUrlPath) {
            navigate(`/events/${event.id}/${tabKey}`, { replace: true });
        }
    };

    // Tabs
    const tabs = [
        {
            key: 'detail',
            caption: t('Detail'),
            component: (
                <EventDetails
                    item={event as any}
                    onSave={(newEvent: any) => {
                        const _event = {
                            ...newEvent,
                            categories: newEvent.categories || event.categories,
                        };
                        setEvent(_event);
                        onChange?.(_event);
                    }}
                    onDelete={(newEvent: any) => {
                        const _event = {
                            ...newEvent,
                            trash: 1,
                            categories: newEvent.categories || event.categories,
                        };
                        onChange?.(_event);
                        onClose?.();
                    }}
                />
            ),
        },
        {
            key: 'categories',
            caption: t('Categories'),
            component: (
                <EventCategories
                    items={event && event.categories}
                    onChange={(categories: Category[]) => {
                        setEvent({ ...event, categories });
                    }}
                    event={event as any}
                />
            ),
            show: !!(event && event.id),
        },
        {
            key: 'participants',
            caption: t('Participants'),
            component: <EventParticipants event={event as any} />,
            show: !!(event && event.id && !event.teamable),
        },
        {
            key: 'teams',
            caption: t('Teams'),
            component: <EventTeams event={event as any} />,
            show: !!(event && event.id && event.teamable),
        },
        {
            key: 'timetables',
            caption: t('Timetables'),
            component: <EventPeriods event={event as any} />,
            show: !!(event && event.id),
        },
        {
            key: 'form',
            caption: t('Form'),
            component: (
                <EventForm
                    event={event as any}
                    onChange={(form: unknown[]) => {
                        setEvent({ ...event, form });
                        onChange?.({ ...event, form } as EventType);
                    }}
                />
            ),
            show: !!(event && event.id),
        },
        {
            key: 'documents',
            caption: t('Documents'),
            component: <EventDocuments event={event as any} />,
            show: !!(event && event.id),
        },
    ];

    return (
        <SttFullDialog
            open
            onClose={onClose}
            title={id === 'create' ? t('CreateEvent') : event.name}
            urlPath={
                disableUrlPath || id === 'create' ? undefined : `/events/${id}`
            }
        >
            <SttTabs
                tabs={tabs}
                initialTab={computedInitialTab}
                onTabChange={handleTabChange}
            />
        </SttFullDialog>
    );
}
